import { DataEntry, LedgerAccount } from './types';
import { differenceInDays } from 'date-fns';
import { lookupAccount } from './helpers';

type FilteredMatchingTransactionsParams = {
  matchingAmountTransactions: DataEntry[];
  dataEntryAccount: LedgerAccount | undefined;
  journalEntry: DataEntry['journalEntry'];
};

type FilteredMatchingTransaction = DataEntry & {
  accountName: string;
};

/**
 * Filters and processes a list of transactions to find potential matches within a specified period.
 *
 *
 * @description
 * This constant determines a list of transactions that match specific criteria:
 * - Excludes transactions if the current entry is an adjustment.
 * - Skips transactions associated with the same account as the current data entry account.
 * - Skips transactions where the vendor name matches the current journal entry's vendor name (e.g., manually created journal entries).
 * - Includes transactions only if the difference in days between the transaction dates is within 10 days.
 *
 * Each matching transaction is augmented with the account name for easier identification.
 */
export const filteredMatchingTransactions = ({
  matchingAmountTransactions,
  dataEntryAccount,
  journalEntry,
}: FilteredMatchingTransactionsParams): FilteredMatchingTransaction[] => {
  const isAdjustment = () => journalEntry.vendorName.toLowerCase().includes('adjustment |');

  return isAdjustment()
    ? []
    : matchingAmountTransactions.reduce((acc: FilteredMatchingTransaction[], transaction) => {
        const account = lookupAccount(transaction);
        if (!account || !dataEntryAccount || account.id === dataEntryAccount.id) return acc;
        // skip if vendor name is same. This is to avoid showing the same transaction as a potential match
        // This happens when a JE is manually created
        if (transaction.journalEntry.vendorName === journalEntry.vendorName) return acc;

        if (
          includesExcludedSubstrings(transaction.journalEntry.vendorName) ||
          includesExcludedSubstrings(journalEntry.vendorName)
        )
          return acc;

        if (
          transaction.journalEntry.internalNotes.some(
            note => note.body === 'Skipped as a MITO consideration',
          )
        ) {
          return acc;
        }

        const daysDifference = Math.abs(
          differenceInDays(
            journalEntry.dateOfTransaction,
            transaction.journalEntry.dateOfTransaction,
          ),
        );

        if (daysDifference <= 10) {
          acc.push({ ...transaction, accountName: account.name });
        }
        return acc;
      }, []);
};

const includesExcludedSubstrings = (vendorName: string): boolean => {
  return excludedVendorNameSubstrings.some(sub => vendorName.includes(sub));
};

const excludedVendorNameSubstrings = [
  'Stripe - Charge',
  'Stripe - Processing Fee',
  'Stripe - Refund',
  'Stripe - Financing Paydown - Withheld funds',
  'Square - CHARGE',
  'Square - Cash on Hand',
  'Square - Cash Sales',
  'Square - Processing Fee',
  'Square - REFUND',
  'Square - SQUARE_CAPITAL_PAYMENT',
  'Square - Tips Collected',
  'Shopify Payments Charge',
  'Shopify Payments Fees',
  'Shopify Payments Refund',
  'Express Checkout Payment',
  'Mass Pay payment',
  'PayPal Fee |',
  'PayPal Working Capital',
];
